/**
 * Calendar page - main view for appointment management
 */

import { PhoneCall, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { SlotInfo } from 'react-big-calendar'
import type { EventInteractionArgs } from 'react-big-calendar/lib/addons/dragAndDrop'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { AcuteDeskDrawer } from '@/components/calendar/AcuteDeskDrawer'
import { Calendar } from '@/components/calendar/Calendar'
import { CalendarToolbar } from '@/components/calendar/CalendarToolbar'
import { SearchResults } from '@/components/calendar/SearchResults'
import { AppointmentDialog } from '@/components/dialogs/AppointmentDialog'
import { CancelledEventsDialog } from '@/components/dialogs/CancelledEventsDialog'
import { useAcuteCaseNotifier } from '@/hooks/useAcuteCaseNotifier'
import { useRescheduleAppointment } from '@/hooks/useAppointmentMutation'
import {
	useCalendarEvents,
	useDayStats,
	useEventSearch,
} from '@/hooks/useCalendarEvents'
import {
	calculateDuration,
	formatDateForApi,
	formatTime,
	getToday,
	getWeekDates,
	getWeekRange,
} from '@/lib/calendar-utils'
import { APPOINTMENT_TYPES, CALENDAR_CONFIG } from '@/lib/constants'
import { trpc } from '@/trpc/client'
import type {
	AppointmentFormData,
	AppointmentType,
	CalendarEvent,
	CalendarView,
	DialogMode,
} from '@/types/calendar'

// View mode type for controlling reception note editability
export type ViewMode = 'zub' | 'dh' | 'reception'

const VOICE_OVERLAY_DISMISSED_KEY = 'voiceAgentOverlayDismissedAt'

// sessionStorage throws SecurityError when storage is disabled (private
// browsing, enterprise policy) — fail open so the overlay convenience
// feature can never white-screen the calendar.
function readDismissedAtCount(): number {
	try {
		return Number(sessionStorage.getItem(VOICE_OVERLAY_DISMISSED_KEY) ?? '0')
	} catch {
		return 0
	}
}

function writeDismissedAtCount(count: number) {
	try {
		sessionStorage.setItem(VOICE_OVERLAY_DISMISSED_KEY, String(count))
	} catch {
		// Storage unavailable — dismissal just won't persist across re-renders.
	}
}

/**
 * Floating bottom-right overlay shown when the phone AI assistant has
 * unprocessed records. Closable; the dismissal remembers the count it was
 * closed at (sessionStorage), so it reappears when NEW records arrive —
 * an urgent case must not stay hidden behind a morning dismissal.
 * The CTA opens /voice-agent in a new tab so the calendar stays put.
 */
function VoiceAgentOverlay() {
	const { data, error } = trpc.voiceAgent.newCount.useQuery(undefined, {
		staleTime: 30000,
		refetchOnWindowFocus: true,
		// Poll so the overlay reappears even when the tab never loses focus —
		// same cadence as the calendar's own auto-refresh.
		refetchInterval: CALENDAR_CONFIG.AUTO_REFRESH_INTERVAL,
	})
	// Bump to re-render after a dismissal writes sessionStorage
	const [, setDismissedTick] = useState(0)
	const [errorDismissed, setErrorDismissed] = useState(false)
	useEffect(() => {
		if (error) console.error('[voice-agent] newCount query failed', error)
	}, [error])

	// This overlay is the last safety net when the reception webhook is also
	// down — a failed count must not render identically to "no new records".
	if (error) {
		if (errorDismissed) return null
		return (
			<div className="fixed bottom-4 right-4 z-50 w-72 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-900 shadow-lg">
				<div className="flex items-start gap-2">
					<PhoneCall className="mt-0.5 h-4 w-4 shrink-0" />
					<div className="min-w-0">
						<p className="font-medium">Hlasový agent</p>
						<p>Nepodařilo se zjistit nové záznamy.</p>
					</div>
					<button
						type="button"
						onClick={() => setErrorDismissed(true)}
						aria-label="Zavřít"
						className="ml-auto rounded p-1 hover:bg-red-100"
					>
						<X className="h-4 w-4" />
					</button>
				</div>
				<a
					href="/voice-agent"
					target="_blank"
					rel="noopener"
					className="mt-2 inline-block font-medium underline underline-offset-2 hover:text-red-700"
				>
					Otevřít v nové kartě
				</a>
			</div>
		)
	}

	if (!data || data.count === 0) return null

	let dismissedAtCount = readDismissedAtCount()
	// The dismissal threshold follows the count downward: close at 3, staff
	// resolve two (count 1), a new case arrives (count 2) — without this the
	// new case would hide behind the stale "3" until the session ended.
	if (data.count < dismissedAtCount) {
		dismissedAtCount = data.count
		writeDismissedAtCount(data.count)
	}
	if (data.count <= dismissedAtCount) return null

	const dismiss = () => {
		writeDismissedAtCount(data.count)
		setDismissedTick((t) => t + 1)
	}

	const label =
		data.count === 1
			? '1 nový záznam'
			: data.count < 5
				? `${data.count} nové záznamy`
				: `${data.count} nových záznamů`

	return (
		<div className="fixed bottom-4 right-4 z-50 w-72 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 shadow-lg">
			<div className="flex items-start gap-2">
				<PhoneCall className="mt-0.5 h-4 w-4 shrink-0" />
				<div className="min-w-0">
					<p className="font-medium">Hlasový agent</p>
					<p>{label} k vyřízení</p>
				</div>
				<button
					type="button"
					onClick={dismiss}
					aria-label="Zavřít"
					className="ml-auto rounded p-1 hover:bg-amber-100"
				>
					<X className="h-4 w-4" />
				</button>
			</div>
			<a
				href="/voice-agent"
				target="_blank"
				rel="noopener"
				className="mt-2 inline-block font-medium underline underline-offset-2 hover:text-amber-700"
			>
				Otevřít v nové kartě
			</a>
		</div>
	)
}

export function CalendarPage() {
	const { type = 'zub' } = useParams<{ type: string }>()
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()

	// Get view mode from URL param (defaults to reception)
	const viewModeParam = searchParams.get('view')
	const viewMode: ViewMode =
		viewModeParam === 'zub' || viewModeParam === 'dh'
			? viewModeParam
			: 'reception'

	// Validate and normalize type
	const calendarType = type.toUpperCase() as AppointmentType
	if (
		calendarType !== APPOINTMENT_TYPES.ZUB &&
		calendarType !== APPOINTMENT_TYPES.DH
	) {
		navigate('/calendar/zub', { replace: true })
	}

	// State
	const [currentDate, setCurrentDate] = useState(getToday())
	const [view, setView] = useState<CalendarView>('week')
	const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null)
	const [isDialogOpen, setIsDialogOpen] = useState(false)
	const [dialogMode, setDialogMode] = useState<DialogMode>('create')
	const [searchQuery, setSearchQuery] = useState('')
	const [activeSearchQuery, setActiveSearchQuery] = useState('')
	const [searchAllTypes, setSearchAllTypes] = useState(false)
	const [isCancelledDialogOpen, setIsCancelledDialogOpen] = useState(false)
	const [isAcuteDeskOpen, setIsAcuteDeskOpen] = useState(false)
	const [cancelledDialogTab, setCancelledDialogTab] = useState<
		'cancelled' | 'noshow'
	>('cancelled')
	const [newEventDefaults, setNewEventDefaults] =
		useState<Partial<AppointmentFormData> | null>(null)
	const [showGhostEvents, setShowGhostEvents] = useState(false)

	// The "other" calendar whose events can be shown as ghosts. ZUB ↔ DH only.
	const ghostType: AppointmentType | null =
		calendarType === APPOINTMENT_TYPES.ZUB
			? APPOINTMENT_TYPES.DH
			: calendarType === APPOINTMENT_TYPES.DH
				? APPOINTMENT_TYPES.ZUB
				: null

	// Calculate date range for current view
	const { start: weekStart, end: weekEnd } = getWeekRange(currentDate)
	const weekDates = getWeekDates(currentDate)

	// Fetch events
	const {
		events: serverEvents,
		isLoading,
		refetch,
	} = useCalendarEvents({
		type: calendarType,
		from: weekStart,
		to: weekEnd,
	})

	// Fetch the other calendar's events for ghost rendering
	const ghostEnabled = showGhostEvents && ghostType !== null
	const { events: ghostEvents } = useCalendarEvents({
		type: ghostType ?? calendarType,
		from: weekStart,
		to: weekEnd,
		enabled: ghostEnabled,
	})

	// Fetch day stats
	const { stats: dayStats } = useDayStats({
		type: calendarType,
		dates: weekDates,
	})

	// Acute-case desk lives on the ZUB calendar only — acute cases are dental.
	const isZubCalendar = calendarType === APPOINTMENT_TYPES.ZUB
	const { unresolvedCount: acuteUnresolvedCount, isStale: acuteCountStale } =
		useAcuteCaseNotifier({
			enabled: isZubCalendar,
			onOpenDrawer: () => setIsAcuteDeskOpen(true),
		})

	// Reschedule mutation for drag & drop
	const reschedule = useRescheduleAppointment()

	// Local state for optimistic updates during drag & drop
	const [optimisticUpdates, setOptimisticUpdates] = useState<
		Map<string, CalendarEvent>
	>(new Map())

	// Merge server events with optimistic updates, and append ghost events when enabled
	const events = useMemo(() => {
		const main =
			optimisticUpdates.size === 0
				? serverEvents
				: serverEvents.map((event) => optimisticUpdates.get(event.id) ?? event)
		return ghostEnabled ? [...main, ...ghostEvents] : main
	}, [serverEvents, optimisticUpdates, ghostEvents, ghostEnabled])

	// Search results - only search when activeSearchQuery is set (via button click)
	const {
		events: searchResults,
		isLoading: isSearching,
		hasNextPage,
		isFetchingNextPage,
		fetchNextPage,
	} = useEventSearch(
		activeSearchQuery,
		searchAllTypes ? undefined : calendarType,
	)

	// Auto-refresh events
	useEffect(() => {
		const interval = setInterval(() => {
			refetch()
		}, CALENDAR_CONFIG.AUTO_REFRESH_INTERVAL)

		return () => clearInterval(interval)
	}, [refetch])

	// Handlers
	const handleNavigate = useCallback((date: Date) => {
		setCurrentDate(date)
	}, [])

	// Keyboard navigation
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			// Don't handle if dialog is open or input is focused
			if (
				isDialogOpen ||
				isCancelledDialogOpen ||
				isAcuteDeskOpen ||
				document.activeElement?.tagName === 'INPUT' ||
				document.activeElement?.tagName === 'TEXTAREA'
			) {
				return
			}

			switch (e.key) {
				case 'ArrowLeft':
					handleNavigate(
						view === 'day'
							? new Date(currentDate.getTime() - 24 * 60 * 60 * 1000)
							: new Date(currentDate.getTime() - 7 * 24 * 60 * 60 * 1000),
					)
					break
				case 'ArrowRight':
					handleNavigate(
						view === 'day'
							? new Date(currentDate.getTime() + 24 * 60 * 60 * 1000)
							: new Date(currentDate.getTime() + 7 * 24 * 60 * 60 * 1000),
					)
					break
				case 't':
				case 'T':
					handleNavigate(getToday())
					break
				case 'Escape':
					setSearchQuery('')
					setActiveSearchQuery('')
					break
			}
		}

		window.addEventListener('keydown', handleKeyDown)
		return () => window.removeEventListener('keydown', handleKeyDown)
	}, [
		currentDate,
		view,
		isDialogOpen,
		isCancelledDialogOpen,
		isAcuteDeskOpen,
		handleNavigate,
	])

	const handleViewChange = useCallback((newView: CalendarView) => {
		setView(newView)
	}, [])

	const handleSelectSlot = useCallback(
		(slotInfo: SlotInfo) => {
			// Create new appointment at this slot
			setSelectedEvent(null)
			setDialogMode('create')
			setNewEventDefaults({
				date: slotInfo.start,
				startTime: formatTime(slotInfo.start),
				endTime: formatTime(slotInfo.end),
				type: calendarType,
			})
			setIsDialogOpen(true)
		},
		[calendarType],
	)

	const handleSelectEvent = useCallback(
		(event: CalendarEvent) => {
			// Ghost events from the other calendar are read-only previews — don't open the dialog.
			if (event.resource?.type !== calendarType) return
			setSelectedEvent(event)
			setDialogMode('edit')
			setNewEventDefaults(null)
			setIsDialogOpen(true)
		},
		[calendarType],
	)

	const handleEventDrop = useCallback(
		(args: EventInteractionArgs<CalendarEvent>) => {
			const { event, start, end } = args
			const startDate = start as Date
			const endDate = end as Date

			// Format time as HH:MM
			const formatTimeStr = (date: Date) => {
				const hours = date.getHours().toString().padStart(2, '0')
				const minutes = date.getMinutes().toString().padStart(2, '0')
				return `${hours}:${minutes}`
			}

			const startTime = formatTimeStr(startDate)
			const endTime = formatTimeStr(endDate)
			const duration = calculateDuration(startTime, endTime)

			// Optimistically update the event position immediately
			const optimisticEvent: CalendarEvent = {
				...event,
				start: startDate,
				end: endDate,
				resource: {
					...event.resource,
					date: formatDateForApi(startDate),
					startTime,
					endTime,
					duration,
				},
			}
			setOptimisticUpdates((prev) =>
				new Map(prev).set(event.id, optimisticEvent),
			)

			// Call the mutation
			reschedule.mutate(
				{
					id: event.id,
					date: formatDateForApi(startDate),
					startTime,
					endTime,
					duration,
				},
				{
					onSettled: () => {
						// Clear optimistic update when server responds (success or error)
						setOptimisticUpdates((prev) => {
							const next = new Map(prev)
							next.delete(event.id)
							return next
						})
					},
				},
			)
		},
		[reschedule],
	)

	const handleCloseDialog = useCallback(() => {
		setIsDialogOpen(false)
		setSelectedEvent(null)
		setNewEventDefaults(null)
	}, [])

	const handleSearch = useCallback((query: string) => {
		setSearchQuery(query)
		// Clear active search when input changes
		if (!query) {
			setActiveSearchQuery('')
		}
	}, [])

	const handleSearchHere = useCallback((query: string) => {
		// Search in current calendar type only
		if (query.length >= 2) {
			setActiveSearchQuery(query)
			setSearchAllTypes(false)
		}
	}, [])

	const handleSearchAll = useCallback((query: string) => {
		// Search across all calendar types
		if (query.length >= 2) {
			setActiveSearchQuery(query)
			setSearchAllTypes(true)
		}
	}, [])

	const handleSearchResultClick = useCallback(
		(event: CalendarEvent) => {
			// Clear search and open the event dialog (without navigating calendar)
			setSearchQuery('')
			setActiveSearchQuery('')
			handleSelectEvent(event)
		},
		[handleSelectEvent],
	)

	const handleOpenCancelled = useCallback(() => {
		setCancelledDialogTab('cancelled')
		setIsCancelledDialogOpen(true)
	}, [])

	const handleCloseCancelled = useCallback(() => {
		setIsCancelledDialogOpen(false)
	}, [])

	const handleOpenNoShow = useCallback(() => {
		setCancelledDialogTab('noshow')
		setIsCancelledDialogOpen(true)
	}, [])

	return (
		<div className="flex h-screen flex-col bg-[#faf8f5]">
			{/* Toolbar */}
			<div className="px-4 pt-4">
				<CalendarToolbar
					currentDate={currentDate}
					view={view}
					onNavigate={handleNavigate}
					onViewChange={handleViewChange}
					onSearch={handleSearch}
					onSearchHere={handleSearchHere}
					onSearchAll={handleSearchAll}
					onOpenCancelled={handleOpenCancelled}
					onOpenNoShow={handleOpenNoShow}
					searchQuery={searchQuery}
					calendarType={calendarType}
					ghostType={ghostType}
					showGhostEvents={ghostEnabled}
					onToggleGhostEvents={() => setShowGhostEvents((v) => !v)}
					onOpenAcuteDesk={
						isZubCalendar ? () => setIsAcuteDeskOpen(true) : undefined
					}
					acuteUnresolvedCount={acuteUnresolvedCount}
					acuteCountStale={acuteCountStale}
				/>

				{/* Search results dropdown - only show when actively searching (after button click) */}
				{activeSearchQuery.length >= 2 && (
					<SearchResults
						results={searchResults}
						isLoading={isSearching}
						onSelect={handleSearchResultClick}
						onClose={() => {
							setSearchQuery('')
							setActiveSearchQuery('')
						}}
						hasNextPage={hasNextPage}
						isFetchingNextPage={isFetchingNextPage}
						fetchNextPage={fetchNextPage}
					/>
				)}
			</div>

			{/* Calendar */}
			<div className="flex-1 overflow-hidden p-4">
				<div className="h-full rounded-lg bg-white shadow-sm">
					<Calendar
						events={events}
						currentDate={currentDate}
						view={view}
						calendarType={calendarType}
						onNavigate={handleNavigate}
						onViewChange={handleViewChange}
						onSelectSlot={handleSelectSlot}
						onSelectEvent={handleSelectEvent}
						onEventDrop={handleEventDrop}
						isLoading={isLoading}
						dayStats={dayStats}
						ghostMode={ghostEnabled}
					/>
				</div>
			</div>

			{/* Appointment Dialog */}
			<AppointmentDialog
				open={isDialogOpen}
				onClose={handleCloseDialog}
				mode={dialogMode}
				event={selectedEvent}
				defaults={newEventDefaults}
				calendarType={calendarType}
				viewMode={viewMode}
				dayStats={dayStats}
			/>

			{/* Cancelled Events Dialog */}
			<CancelledEventsDialog
				open={isCancelledDialogOpen}
				onClose={handleCloseCancelled}
				initialTab={cancelledDialogTab}
			/>

			{/* Acute-case desk drawer (right) — ZUB only */}
			{isZubCalendar && (
				<AcuteDeskDrawer
					open={isAcuteDeskOpen}
					onOpenChange={setIsAcuteDeskOpen}
				/>
			)}

			{/* Voice agent overlay (bottom-right) */}
			<VoiceAgentOverlay />
		</div>
	)
}
